/*
The folio rail — the running head printed down the trim edge.

Any element with a `data-folio` attribute is a section of the issue.
As one takes over the viewport its name and its plate ink move to the
rail, the way a running head changes when you turn a page. The rail
also carries a read-progress bar for the whole document.
*/

const SETTLE = 220;

function readPlate(el) {
    const inline = el.style.getPropertyValue('--plate').trim();
    if (inline) return inline;
    return getComputedStyle(el).getPropertyValue('--plate').trim();
}

export default function folioRail() {
    const sections = Array.from(document.querySelectorAll('[data-folio]'));
    if (!sections.length) return;

    const label = document.querySelector('[data-folio-section]');
    const number = document.querySelector('[data-folio-number]');
    const running = document.querySelector('.folio-running');
    const root = document.documentElement;

    // Progress ---------------------------------------------------------
    let ticking = false;

    function progress() {
        const max = document.body.scrollHeight - window.innerHeight;
        const ratio = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
        root.style.setProperty('--read-progress', ratio.toFixed(4));
        ticking = false;
    }

    window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(progress);
    }, { passive: true });

    progress();

    // Running head ------------------------------------------------------
    if (!label) return;

    let current = null;

    function turnTo(section, index) {
        if (section === current) return;
        current = section;

        const name = section.getAttribute('data-folio');
        const plate = readPlate(section);

        const paint = () => {
            label.textContent = name;
            if (number) {
                number.textContent = String(index + 1).padStart(2, '0');
            }
            if (plate) root.style.setProperty('--plate', plate);
        };

        if (running && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            running.classList.add('is-turning');
            window.setTimeout(() => {
                paint();
                running.classList.remove('is-turning');
            }, SETTLE / 2);
        } else {
            paint();
        }
    }

    const visible = new Map();

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                visible.set(entry.target, entry.intersectionRatio);
            } else {
                visible.delete(entry.target);
            }
        });

        if (!visible.size) return;

        // The section occupying most of the reading band wins
        let best = null;
        let bestRatio = -1;
        visible.forEach((ratio, el) => {
            if (ratio > bestRatio) {
                bestRatio = ratio;
                best = el;
            }
        });

        if (best) turnTo(best, sections.indexOf(best));
    }, {
        // Watch the middle band of the viewport, not its edges
        rootMargin: '-30% 0px -45% 0px',
        threshold: [0, 0.15, 0.4, 0.75, 1]
    });

    sections.forEach((section) => observer.observe(section));
}
